import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { CreditCard } from '@liveauctioneers/caterwaul-components/lib/types/CreditCard';
import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import React from 'react';
import styled, { css } from 'styled-components';

import { PaymentMethods, PaymentProviders } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { getCreditCardError } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import {
    getCatalogHighRiskPayments,
    getCatalogPaymentProvider,
    getCatalogRequiredACH,
    getCatalogSellerName,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';

import PaymentMethodACH from './components/PaymentMethodACH';
import PaymentMethodCards from './components/PaymentMethodCards';
import PaymentMethodZip from './components/PaymentMethodZip';

type Props = {
    active: boolean;
    amountToPayZip: number;
    bidderPaysProcessingFee: boolean;
    catalogId: number;
    currency?: string;
    hadZipError: boolean;
    onBankAccountSelectionChange: (bankAccount: BankAccount) => void;
    onCardSelectionChange: (creditCard: CreditCard) => void;
    onPaymentMethodChange: (paymentMethod: PaymentMethods) => void;
    paymentMethod: PaymentMethods;
    requireACHForCheckout: boolean;
    selectedBankAccount: BankAccount;
    selectedCreditCard: CreditCard;
    showAchPaymentOption: boolean;
    showCardPaymentOption: boolean;
    showZipOption: boolean;
};

const Payment = ({
    active,
    amountToPayZip,
    bidderPaysProcessingFee,
    catalogId,
    currency,
    hadZipError,
    onBankAccountSelectionChange,
    onCardSelectionChange,
    onPaymentMethodChange,
    paymentMethod,
    requireACHForCheckout,
    selectedBankAccount,
    selectedCreditCard,
    showAchPaymentOption,
    showCardPaymentOption,
    showZipOption,
}: Props) => {
    const { formatMessage, formatNumber } = useIntl();

    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const catalogHighRiskPayments = useAppSelector((state) => getCatalogHighRiskPayments(state, catalogId));
    const laPaymentCardError = useAppSelector(getCreditCardError);
    const requiredAch = useAppSelector((state) => getCatalogRequiredACH(state, catalogId));
    const houseName = useAppSelector((state) => getCatalogSellerName(state, catalogId));

    let infoMessage;
    if (
        catalogHighRiskPayments &&
        !(
            selectedCreditCard &&
            (paymentProvider === PaymentProviders.Paysafe || paymentProvider === PaymentProviders.Payrix)
        )
    ) {
        infoMessage = <FormattedMessage id="checkout.payment.highRisk.editRegulated" />;
    }
    if (requireACHForCheckout) {
        // When requiredACH is 1 cent, all invoices must use ach and we want a slightly different message
        infoMessage = formatMessage(
            {
                id: requiredAch === 1 ? 'checkout.payment.requiredAchAll' : 'checkout.payment.requiredAch',
            },
            {
                amount: formatNumber(requiredAch / 100, {
                    currency,
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                    style: 'currency',
                }),
                house: houseName,
            }
        );
    }

    if (laPaymentCardError) {
        return (
            <BodyPrimary color="critical">
                <FormattedMessage
                    defaultMessage={laPaymentCardError}
                    id={laPaymentCardError}
                />
            </BodyPrimary>
        );
    }
    return (
        <StyledPayments data-testid="payment-step">
            {infoMessage && (
                <StyledInfoMessage>
                    <StyledInfoIconWrapper>
                        <Info
                            className="inline-flex"
                            color="primary"
                            size="sm"
                            style="fill"
                        />
                    </StyledInfoIconWrapper>
                    <BodySecondary>{infoMessage}</BodySecondary>
                </StyledInfoMessage>
            )}
            {active && hadZipError && (
                <StyledErrorMessage>
                    <BodySecondary color="critical">
                        <FormattedMessage id="checkout.payment.zipError" />
                    </BodySecondary>
                    <Link to="https://help.liveauctioneers.com/">
                        <FormattedMessage id="checkout.general.learnMore" />
                    </Link>
                </StyledErrorMessage>
            )}
            <StyledPaymentMethods $active={active}>
                {showCardPaymentOption && (
                    <PaymentMethodCards
                        active={active}
                        catalogId={catalogId}
                        onCardSelectionChange={onCardSelectionChange}
                        onPaymentMethodChange={onPaymentMethodChange}
                        paymentMethod={paymentMethod}
                        selectedCreditCard={selectedCreditCard}
                    />
                )}
                {showAchPaymentOption && (
                    <PaymentMethodACH
                        active={active}
                        bidderPaysProcessingFee={bidderPaysProcessingFee}
                        catalogId={catalogId}
                        currency={currency}
                        onBankAccountSelectionChange={onBankAccountSelectionChange}
                        onPaymentMethodChange={onPaymentMethodChange}
                        paymentMethod={paymentMethod}
                        requireACHForCheckout={requireACHForCheckout}
                        selectedBankAccount={selectedBankAccount}
                    />
                )}
                {showZipOption && (
                    <PaymentMethodZip
                        active={active}
                        amountToPayZip={amountToPayZip}
                        currency={currency}
                        onPaymentMethodChange={onPaymentMethodChange}
                        paymentMethod={paymentMethod}
                    />
                )}
            </StyledPaymentMethods>
        </StyledPayments>
    );
};

export default Payment;

const StyledPayments = styled.div`
    width: 100%;
`;

const StyledPaymentMethods = styled.div<{ $active: boolean }>`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows:
        [tabs] auto
        [content] auto;
    grid-column-gap: 16px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        grid-template-columns: 1fr;
        grid-template-rows: auto;
        grid-column-gap: 0;
    }

    /* when not active, we want the display to take up all the width */
    ${({ $active }) =>
        !$active &&
        css`
            grid-template-columns: 1fr;
        `};
`;

const StyledInfoMessage = styled.div`
    display: flex;
    padding-bottom: 24px;
`;

const StyledErrorMessage = styled(StyledInfoMessage)`
    flex-direction: column;
`;

const StyledInfoIconWrapper = styled.div`
    margin-right: 8px;
`;
